/**
 * Redux thunks for course ratings
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCourseRatings,
  getUserRating,
  saveRating as saveRatingService,
  deleteRating as deleteRatingService,
  calculateAverageRating,
} from '../../services/firestore/ratingService';
import { CourseRating } from '@e-learning/shared/src/store/types';

/**
 * Fetch all ratings for a course
 */
export const fetchCourseRatings = createAsyncThunk(
  'ratings/fetchCourseRatings',
  async (courseId: string, { rejectWithValue }) => {
    try {
      const ratings = await getCourseRatings(courseId);
      return { courseId, ratings };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch ratings');
    }
  }
);

/**
 * Fetch user's rating for a course
 */
export const fetchUserRating = createAsyncThunk(
  'ratings/fetchUserRating',
  async ({ courseId, userId }: { courseId: string; userId: string }, { rejectWithValue }) => {
    try {
      const rating = await getUserRating(courseId, userId);
      return { courseId, rating };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch user rating');
    }
  }
);

/**
 * Submit a rating
 */
export const submitRating = createAsyncThunk(
  'ratings/submitRating',
  async (
    { courseId, userId, rating }: { courseId: string; userId: string; rating: number },
    { rejectWithValue }
  ) => {
    try {
      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }
      const savedRating = await saveRatingService({
        courseId,
        userId,
        rating,
      });
      // Fetch updated ratings to recalculate average
      const allRatings = await getCourseRatings(courseId);
      const average = calculateAverageRating(allRatings);
      return { rating: savedRating, averageRating: average, totalRatings: allRatings.length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit rating');
    }
  }
);

/**
 * Delete a rating
 */
export const removeRating = createAsyncThunk(
  'ratings/removeRating',
  async ({ courseId, userId }: { courseId: string; userId: string }, { rejectWithValue }) => {
    try {
      await deleteRatingService(courseId, userId);
      // Fetch updated ratings to recalculate average
      const allRatings = await getCourseRatings(courseId);
      const average = calculateAverageRating(allRatings);
      return { courseId, averageRating: average, totalRatings: allRatings.length };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to remove rating');
    }
  }
);

